import Vue from "vue";

import { Dictionary } from "@/lib/Dictionary.type";
import IdGenerator from "@/lib/services/id-generator";

import ProductAllocation from "../product-allocation.model";
import { ProductAllocationSlotTypeValue } from "../product-allication-slot-type.value";
import UpgradeAllocation from "../upgrade-allocation.model";

export interface AllocationBaseline<T> {
  id?: string;
  isConfigured: boolean;
  value: T;
}

export interface AllocationEntity {
  id: string;
  name: string;
}

export type AllocationOrientation = "item" | "factory";

export type AllocationDetailStatus = "enabled" | "disabled" | "unconfigured";

export const minAllocationPercent = 0;
export const maxAllocationPercent = 100;

export function isValidAllocationPercent(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= minAllocationPercent &&
    value <= maxAllocationPercent
  );
}

export function productAllocationKey(
  slotType: ProductAllocationSlotTypeValue,
  factoryId: string,
  productId: string
): string {
  return `${slotType}:${factoryId}:${productId}`;
}

export function upgradeAllocationKey(
  factoryId: string,
  upgradeId: string
): string {
  return `${factoryId}:${upgradeId}`;
}

export function createProductAllocationBaseline(
  allocations: ProductAllocation[],
  slotType: ProductAllocationSlotTypeValue
): Dictionary<AllocationBaseline<number>> {
  return allocations.reduce((index, allocation) => {
    if (allocation.slotType !== slotType) {
      return index;
    }

    const key = productAllocationKey(
      slotType,
      allocation.factory,
      allocation.product
    );
    index[key] = {
      id: allocation.id,
      isConfigured: true,
      value: allocation.distributionPercent,
    };
    return index;
  }, {} as Dictionary<AllocationBaseline<number>>);
}

export function createUpgradeAllocationBaseline(
  allocations: UpgradeAllocation[]
): Dictionary<AllocationBaseline<boolean>> {
  return allocations.reduce((index, allocation) => {
    const key = upgradeAllocationKey(allocation.factory, allocation.upgrade);
    index[key] = {
      id: allocation.id,
      isConfigured: true,
      value: allocation.canProduce,
    };
    return index;
  }, {} as Dictionary<AllocationBaseline<boolean>>);
}

export class SparseAllocationDraft<T> {
  public readonly drafts: Dictionary<T> = {};
  public readonly staged: Dictionary<boolean> = {};

  constructor(
    private fBaseline: Dictionary<AllocationBaseline<T>>,
    private readonly fDefaultValue: T
  ) {}

  get isDirty(): boolean {
    return Object.keys(this.drafts).length > 0;
  }

  get hasStagedDetails(): boolean {
    return Object.keys(this.staged).length > 0;
  }

  get dirtyKeys(): string[] {
    return Object.keys(this.drafts);
  }

  get baselineKeys(): string[] {
    return Object.keys(this.fBaseline);
  }

  public getBaseline(key: string): AllocationBaseline<T> {
    return (
      this.fBaseline[key] || {
        isConfigured: false,
        value: this.fDefaultValue,
      }
    );
  }

  public getEffectiveValue(key: string): T {
    return Object.prototype.hasOwnProperty.call(this.drafts, key)
      ? this.drafts[key]
      : this.getBaseline(key).value;
  }

  public isModified(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.drafts, key);
  }

  public isConfigured(key: string): boolean {
    return this.getBaseline(key).isConfigured || this.isModified(key);
  }

  public isStaged(key: string): boolean {
    return !!this.staged[key];
  }

  public stage(key: string): void {
    if (!this.getBaseline(key).isConfigured) {
      this.setValue(key, this.fDefaultValue);
    }
    Vue.set(this.staged, key, true);
  }

  public setValue(key: string, value: T): void {
    const baseline = this.getBaseline(key);
    if (baseline.isConfigured && value === baseline.value) {
      Vue.delete(this.drafts, key);
      return;
    }

    Vue.set(this.drafts, key, value);
  }

  public replaceBaseline(baseline: Dictionary<AllocationBaseline<T>>): void {
    this.fBaseline = baseline;
    this.clearDrafts();
  }

  public clearDrafts(): void {
    Object.keys(this.drafts).forEach((key) => Vue.delete(this.drafts, key));
    Object.keys(this.staged).forEach((key) => Vue.delete(this.staged, key));
  }
}

export function materializeProductAllocations(
  slotType: ProductAllocationSlotTypeValue,
  products: AllocationEntity[],
  factories: AllocationEntity[],
  state: SparseAllocationDraft<number>,
  idGenerator: IdGenerator,
  canCreate: (factoryId: string) => boolean = () => true
): ProductAllocation[] {
  const allocations: ProductAllocation[] = [];
  const included = new Set<string>();

  const add = (factoryId: string, productId: string): void => {
    const key = productAllocationKey(slotType, factoryId, productId);
    if (included.has(key)) return;
    const baseline = state.getBaseline(key);
    if (!baseline.isConfigured && !canCreate(factoryId)) return;
    included.add(key);
    allocations.push(new ProductAllocation(
      baseline.id || idGenerator.getId(), factoryId, productId,
      state.getEffectiveValue(key), slotType
    ));
  };

  products.forEach((product) => {
    factories.forEach((factory) => {
      add(factory.id, product.id);
    });
  });

  state.baselineKeys.concat(state.dirtyKeys).forEach((key) => {
    const [slot, factoryId, productId] = key.split(":");
    if (slot === slotType && factoryId && productId) add(factoryId, productId);
  });

  return allocations;
}

export function materializeUpgradeAllocations(
  upgrades: AllocationEntity[],
  factories: AllocationEntity[],
  state: SparseAllocationDraft<boolean>,
  idGenerator: IdGenerator
): UpgradeAllocation[] {
  const allocations: UpgradeAllocation[] = [];
  const included = new Set<string>();
  const activeFactories = new Set(factories.map((factory) => factory.id));

  const add = (factoryId: string, upgradeId: string): void => {
    const key = upgradeAllocationKey(factoryId, upgradeId);
    if (included.has(key)) return;
    included.add(key);
    const baseline = state.getBaseline(key);
    allocations.push(
      new UpgradeAllocation(
        baseline.id || idGenerator.getId(),
        factoryId,
        upgradeId,
        state.getEffectiveValue(key)
      )
    );
  };

  upgrades.forEach((upgrade) => {
    factories.forEach((factory) => {
      add(factory.id, upgrade.id);
    });
  });

  state.baselineKeys.forEach((key) => {
    const [factoryId, upgradeId] = key.split(":");
    if (activeFactories.has(factoryId) && upgradeId) add(factoryId, upgradeId);
  });

  return allocations;
}
